/**
 * Carrying "the lobby a signed-out visitor was invited to" across an OAuth
 * redirect.
 *
 * `lobby_live`'s WebSocket upgrade and every lobby HTTP route require a
 * session, so a stranger who follows a shared "#lobby/<id>" link before
 * signing in has to go through sign-in first — and the OAuth round trip
 * leaves the page for the provider's own and back with the hash gone (see
 * router.ts's note on camo/editor.ts's own hasPendingCamo() for why). The
 * camo editor already solved "carry state across that redirect" for its own
 * unsaved-work case; this is the same mechanism, once removed and pointed at
 * a bare id instead of a whole camo.
 *
 * Its own module, importing nothing, rather than living in
 * screens/lobby-invite.ts alongside the splash screen that uses it: router.ts
 * needs to peek and consume the stash on every landing regardless of which
 * screen ends up rendered, and — the more mechanical reason — this file's
 * lack of imports is what lets `node --test` load it directly. Node's native
 * TypeScript support resolves an extensionless local import the way a
 * bundler does only for a file that has none of its own; `screens/lobby-
 * invite.ts` imports `../chrome`, `../dom` and `../signin-form`, none of
 * which `node --test` can resolve without a build step, so the logic worth
 * asserting for real (peek without consuming, then consume once) has to live
 * somewhere reachable without them.
 */

const PENDING_KEY = "lance.blue:lobby:pending";

/**
 * Set immediately before the OAuth redirect fires, from the splash screen's
 * own sign-in form (onBeforeConnect). There is nothing to run a cleanup on
 * failure — the tab is about to leave for the provider — so this is
 * set-and-forget the same way camo's RESUME_KEY is.
 */
export function stashLobbyInvite(matchId: string): void {
  try {
    localStorage.setItem(PENDING_KEY, matchId);
  } catch {
    /* Private mode or a full quota: the invite goes unrestored and sign-in
     * still works — the visitor lands on Home instead of back in the lobby. */
  }
}

/**
 * Whether a stash exists, without consuming it. routeFromLocation() only
 * ever peeks: deciding the route must never by itself discard a stash
 * nothing has used yet. Mirrors camo/editor.ts's hasPendingCamo().
 */
export function hasPendingLobbyInvite(): boolean {
  try {
    return localStorage.getItem(PENDING_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Reads and clears the stash in one step. Called exactly once per landing on
 * the "lobby" route (router.ts's start()), whether or not the id it returns
 * ends up being the one actually used — a direct "#lobby/<id>" visit clears
 * a stale stash left by an abandoned sign-in the same way entering the camo
 * editor unconditionally clears RESUME_KEY, so an old invite cannot hijack
 * an unrelated later sign-in.
 */
export function takePendingLobbyInvite(): string | null {
  let id: string | null = null;
  try {
    id = localStorage.getItem(PENDING_KEY);
  } catch {
    /* ignored, same as stashLobbyInvite */
  }
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignored */
  }
  return id;
}
